import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Hardcoded data for courses
const coursesData = [
  {
    id: 1,
    title: 'Introduction to Phishing',
    description: 'Learn the basics of phishing attacks and how to identify them.',
    modules: 4,
    status: 'published',
    category: 'Fundamentals',
    createdAt: '2023-05-15',
    updatedAt: '2023-07-22',
    enrollments: 198,
    completionRate: 85,
  },
  {
    id: 2,
    title: 'Email Security Fundamentals',
    description: 'Master the essentials of email security and protection against common threats.',
    modules: 5,
    status: 'published',
    category: 'Email Security',
    createdAt: '2023-06-10',
    updatedAt: '2023-08-05',
    enrollments: 175,
    completionRate: 72,
  },
  {
    id: 3,
    title: 'Advanced Phishing Techniques',
    description: 'Explore sophisticated phishing methods and advanced defense strategies.',
    modules: 6,
    status: 'draft',
    category: 'Advanced',
    createdAt: '2023-09-18',
    updatedAt: '2023-10-12',
    enrollments: 0,
    completionRate: 0,
  },
  {
    id: 4,
    title: 'Social Engineering Defense',
    description: 'Understand social engineering tactics and develop effective countermeasures.',
    modules: 5,
    status: 'published',
    category: 'Social Engineering',
    createdAt: '2023-07-25',
    updatedAt: '2023-09-30',
    enrollments: 124,
    completionRate: 68,
  },
  {
    id: 5,
    title: 'Mobile Device Security',
    description: 'Learn how to protect your mobile devices from phishing and other threats.',
    modules: 3,
    status: 'published',
    category: 'Device Security',
    createdAt: '2023-08-12',
    updatedAt: '2023-09-05',
    enrollments: 145,
    completionRate: 91,
  },
];

// Hardcoded categories
const categoriesData = [
  { id: 1, name: 'Fundamentals', courseCount: 3 },
  { id: 2, name: 'Email Security', courseCount: 2 },
  { id: 3, name: 'Advanced', courseCount: 1 },
  { id: 4, name: 'Social Engineering', courseCount: 1 },
  { id: 5, name: 'Device Security', courseCount: 1 },
];

const ContentManagement = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  
  // Form state for adding course
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'Fundamentals',
    status: 'draft',
  });
  
  useEffect(() => {
    // Simulate API calls
    setTimeout(() => {
      setCourses(coursesData);
      setCategories(categoriesData);
      setIsLoading(false);
    }, 1000);
  }, []);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleCourseFormChange = (e) => {
    const { name, value } = e.target;
    setCourseForm({
      ...courseForm,
      [name]: value,
    });
  };
  
  const handleAddCourse = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!courseForm.title || !courseForm.description) {
      alert('Title and description are required');
      return;
    }
    
    // Simulate API call to add course
    const newCourse = {
      id: courses.length + 1,
      ...courseForm,
      modules: 0,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      enrollments: 0,
      completionRate: 0,
    };
    
    setCourses([...courses, newCourse]);
    setIsAddingCourse(false);
    
    // Reset form
    setCourseForm({
      title: '',
      description: '',
      category: 'Fundamentals',
      status: 'draft',
    });
    
    alert('Course added successfully!');
  };
  
  const cancelAddCourse = () => {
    setIsAddingCourse(false);
    setCourseForm({
      title: '',
      description: '',
      category: 'Fundamentals',
      status: 'draft',
    });
  };
  
  const handlePublishCourse = (courseId) => {
    const updatedCourses = courses.map(course => {
      if (course.id === courseId) {
        return {
          ...course,
          status: 'published',
          updatedAt: new Date().toISOString().split('T')[0],
        };
      }
      return course;
    });
    
    setCourses(updatedCourses);
    alert('Course published successfully!');
  };
  
  const handleDuplicateCourse = (courseId) => {
    const courseToDuplicate = courses.find(course => course.id === courseId);
    if (!courseToDuplicate) return;
    
    const newCourse = {
      ...courseToDuplicate,
      id: courses.length + 1,
      title: `${courseToDuplicate.title} (Copy)`,
      status: 'draft',
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      enrollments: 0,
      completionRate: 0,
    };
    
    setCourses([...courses, newCourse]);
    alert('Course duplicated successfully!');
  };
  
  // Filter courses based on search and filters
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || course.status === statusFilter;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Content Management
        </h1>
        <div>
          <button 
            onClick={() => setIsAddingCourse(true)}
            className="btn btn-primary"
          >
            Add Course
          </button>
        </div>
      </div>
      
      {/* Add Course Form */}
      {isAddingCourse && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Course</h2>
          <form onSubmit={handleAddCourse} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Course Title
              </label>
              <input
                type="text"
                name="title"
                id="title"
                value={courseForm.title}
                onChange={handleCourseFormChange}
                className="input mt-1"
                required
              />
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows="3"
                value={courseForm.description}
                onChange={handleCourseFormChange}
                className="input mt-1"
                required
              ></textarea>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  name="category"
                  id="category"
                  value={courseForm.category}
                  onChange={handleCourseFormChange}
                  className="input mt-1"
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>{category.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  name="status"
                  id="status"
                  value={courseForm.status}
                  onChange={handleCourseFormChange}
                  className="input mt-1"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelAddCourse}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                Add Course
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Content Filters */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search Courses
            </label>
            <input
              type="text"
              id="search"
              placeholder="Search by title or description"
              value={searchTerm}
              onChange={handleSearchChange}
              className="input"
            />
          </div>
          
          <div>
            <label htmlFor="categoryFilter" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="categoryFilter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.name}>{category.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Courses List */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Courses</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Modules
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enrollments
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCourses.map((course) => (
                <tr key={course.id} 
                    className={selectedCourseId === course.id ? 'bg-blue-50' : ''}
                    onClick={() => setSelectedCourseId(course.id === selectedCourseId ? null : course.id)}>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">{course.title}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{course.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{course.category}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{course.modules}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${course.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {course.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {course.enrollments} 
                      {course.completionRate > 0 && (
                        <span className="text-xs text-gray-500 ml-1">
                          ({course.completionRate}% completed)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{course.updatedAt}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link to={`/admin/content/${course.id}`} className="text-primary-600 hover:text-primary-900 mr-3">
                      Edit
                    </Link>
                    {course.status === 'draft' && (
                      <button 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handlePublishCourse(course.id); 
                        }}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        Publish
                      </button>
                    )}
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleDuplicateCourse(course.id); 
                      }}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Duplicate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No courses found matching your search criteria.</p>
          </div>
        )}
      </div>
      
      {/* Categories Section */}
      <div className="card mt-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
          <button className="btn btn-outline text-sm">
            Manage Categories
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {categories.map(category => (
            <div key={category.id} className="border border-gray-200 rounded-md p-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-gray-900">{category.name}</h3>
                <span className="text-xs text-gray-500">{category.courseCount} courses</span>
              </div>
              <button 
                onClick={() => setCategoryFilter(category.name)}
                className="text-sm text-primary-600 hover:text-primary-800 mt-2"
              >
                View Courses
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentManagement; 